import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { P3Desk } from "../../styles/styles_text";
import { Card } from "../../components/ui/card";
import NewFilter from "../../assets/kinologyIcons/svg/new_filter.svg";
import WithFilter from "../../assets/kinologyIcons/svg/with_filter.svg";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Rectangle,
	XAxis,
	YAxis,
} from "recharts";
import {
	ChartConfig,
	ChartContainer,
	ChartTooltip,
} from "../../components/ui/chart";
import SearchUsers from "./SearchUsers";
import { useSelector } from "react-redux";
import Filter from "./Filter";
import EmptyComponent from "./EmptyComponent";

interface DataShema {
	name_risco: string;
	valor_risco: string;
	fill: string;
	usuarios: any[];
}

interface DonutChartProps {
	title: string;
	data: Array<DataShema> | [];
	onDonutChange: (selected: string[]) => void;
	defaultFilter: any;
	hiddenFilter?: boolean
}

const LegendaAlteracoes: React.FC = () => (
	<LegendaContainer>
		<LinhaLegenda>
			<Circulo cor="#7CFFA2" />
			<div>
				<TituloLegenda>Sem alterações</TituloLegenda>
				<TextoLegenda>
					<b>Não há alterações</b> no relatório de assimetria.
				</TextoLegenda>
			</div>
		</LinhaLegenda>
		<LinhaLegenda>
			<Circulo cor="#FFE066" />
			<div>
				<TituloLegenda>Alterações leves</TituloLegenda>
				<TextoLegenda>
					Alterações <b>consideráveis</b>, onde a região com maior alteração possui <b>Assimetria entre 10-20%.</b>
				</TextoLegenda>
			</div>
		</LinhaLegenda>
		<LinhaLegenda>
			<Circulo cor="#FFA800" />
			<div>
				<TituloLegenda>Alterações significativas</TituloLegenda>
				<TextoLegenda>
					Alterações <b>expressivas</b>, onde a região com maior alteração possui <b>Assimetria entre 20-35%.</b>
				</TextoLegenda>
			</div>
		</LinhaLegenda>
		<LinhaLegenda>
			<Circulo cor="#FF3B1D" />
			<div>
				<TituloLegenda>Alterações graves</TituloLegenda>
				<TextoLegenda>
					Alterações <b>altamente expressivas</b>, onde a região com maior alteração possui <b>Assimetria acima de 35%.</b>
				</TextoLegenda>
			</div>
		</LinhaLegenda>
	</LegendaContainer>
);

const AlteracoesEncontradas: React.FC<DonutChartProps> = ({
	title,
	data,
	onDonutChange,
	defaultFilter,
	hiddenFilter=false,
	
}) => {
	const [isTooltipVisible, setIsTooltipVisible] = useState(false);

	const [with_filter, set_with_filter] = useState(false);

	const [activeIndex, setActiveIndex]: any = useState(null);

	const onFilterChange = (selected: any) => {
		const aux_filter = Object.entries(selected);
		set_with_filter(aux_filter.length > 0 ? true : false);
		onDonutChange(selected);
	};

	const [total_users, set_total_users]: any = useState(0)
	useEffect(() => {
		set_total_users(data?.map((risco: any) => risco?.usuarios?.length).reduce((acumulador, valorAtual) => acumulador + valorAtual, 0))
	}, [data])

	const chartConfig = {
		valor_risco: {
			label: "Visitors",
		},

		"Fora do padrão": {
			label: "Padrão esperado",
			color: "hsl(var(--chart-5))",
		},
	} satisfies ChartConfig;

	function _renderBar() {
		return (

			<ChartContainer
				config={chartConfig}
				style={{ height: "250px", width: "80%" }}
				onMouseLeave={() => setIsTooltipVisible(false)}
			>
				{
					data?.some((item)=>item?.usuarios?.length > 0)
					?
						<BarChart accessibilityLayer data={data}>
							<CartesianGrid vertical={false} strokeDasharray="5 5" />
							<XAxis
								dataKey="name_risco"
								tickLine={false}
								tickMargin={10}
								axisLine={false}
								interval={0}

								tick={({ x, y, payload }) => {
									const words = payload.value.split(' ');
									return (
										<g transform={`translate(${x},${y})`}>

											{words.map((word: any, index: any) => (
												<text
													key={index}
													dy={index === 0 ? 0 : 15}
													dx={-20}
													style={{ fontSize: '0.55vw' }}
												>
													{word}
												</text>
											))}

										</g>
									);
								}}
							/>
							<YAxis
								tickLine={false}
								tickMargin={10}
								axisLine={false}
								tick={({ x, y, payload }) => {
									return (
										<g transform={`translate(${x},${y})`}>
											<text
												x={0}
												y={0}
												dy={3} // deslocamento vertical
												dx={10}
												textAnchor="end"
												style={{
													fontSize: 12,
													fill: "rgba(128, 128, 128, 0.69)",
												}} // Altere o tamanho da fonte aqui
											>
												{payload.value}
											</text>
										</g>
									);
								}}
							/>
							<ChartTooltip
								cursor={{ fill: 'rgba(0, 0, 0, 0)' }}
								active={isTooltipVisible}
								content={<SearchUsers />}
								trigger="click"
							/>
							<Bar
								dataKey="valor_risco"
								fill="fill" radius={8}
								barSize={40}
								onClick={() => setIsTooltipVisible(true)}
								onMouseEnter={(_, index) => setActiveIndex(index)}
								onMouseLeave={() => setActiveIndex(null)}
								activeIndex={activeIndex}
								activeBar={({ ...props }) => {
									return (
										<Rectangle
											{...props}
											fillOpacity={0.7}
											strokeDasharray={4}
											strokeDashoffset={4}
										/>
									)
								}}
							/>
						</BarChart>
					: 
						<div className={`flex w-full h-full justify-center`}>
							<EmptyComponent />
						</div>
				}
			</ChartContainer>

		);
	}

	const assimetrias_dynalabs = useSelector(
		(state: any) => state.dashboard.assimetrias_dynalabs
	);

	return (
		<Card
			style={{
				display: "flex",
				flexDirection: "row",
				backgroundColor: "#FFFFFF",
				width: "100%",
				marginBottom: "0.75rem",
				marginTop: "0.75rem",
			}}
		>
			<div
				style={{
					width: "60%",
					position: "relative",
					display: "flex",
					flexDirection: "column",
					padding: "1rem",
				}}
			>
				<P3Desk
					style={{
						textAlign: "center",
						paddingLeft: "2rem",
						paddingRight: "2rem",
						fontSize: "1rem",
						color: "#63677A",
					}}
				>
					{title}
				</P3Desk>
				<P3Desk
					style={{
						textAlign: "center",
						margin: '0.5rem'
					}}
				>
					{total_users} avaliados
				</P3Desk>

				<div style={{ display: hiddenFilter ? 'none' : 'flex',  position: "absolute", top: "1rem", right: "1rem" }}>
					<Filter
						icon={with_filter ? WithFilter : NewFilter}
						onFilterChange={onFilterChange}
						defaultFilter={defaultFilter}
						GraphType={title}
						Cleanedfilter={()=> set_with_filter(false)}
					/>
				</div>

				<div
					style={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{_renderBar()}
				</div>
			</div>
			<div
				style={{
					width: "40%",
					backgroundColor: "#F8F8F8",
					borderRadius: "10px",
					borderTopLeftRadius: "28px",
					borderBottomLeftRadius: "28px",
				}}
			>
				<LegendaAlteracoes />
			</div>
		</Card>
	);
};

const Color = styled.div``;

const LegendaContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 2rem 1.5rem;
`;

const LinhaLegenda = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 0.8rem;
`;

const Circulo = styled.div<{ cor: string }>`
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: ${({ cor }) => cor};
	margin-top: 4px;
	flex-shrink: 0;
`;

const TituloLegenda = styled.span`
	font-weight: 500;
	font-size: 0.8rem;
	color: #797D8D;
`;

const TextoLegenda = styled.span`
	font-size: 0.7rem;
	color: #5a5e6a;
	display: block;
`;

export default AlteracoesEncontradas;
